// StaffMessages.js
// =============================================================================
// Communications → Messages
// =============================================================================
//
// Two panels:
//   LEFT   every member of staff in scope — name, role, branch — with a count
//          of what they have been sent and when. Searchable, filterable by
//          role and branch.
//   RIGHT  that person's message history, newest first, each row showing how
//          it went (email or push notification), where it came from, what it
//          said and whether it actually arrived.
//
// The history is read from pharmace_common_db.staff_messages — the unified
// store added in staff_messages.js. Every send point in the app records there,
// so this pane shows the whole picture rather than just the messages sent from
// this screen. See STAFF-MESSAGES-WIRING.md for which send points are wired.
//
// A NOTE ON WHAT YOU WILL SEE ON DAY ONE
// --------------------------------------
// staff_messages is populated going forward, not backfilled. Existing history
// lives in four incompatible places (staff_schedule_invites, flags on the
// appraisal documents, the rota circulation log, and nowhere at all for
// notifications), and reconstructing it would mean inventing send times and
// bodies we do not hold. So the pane starts empty and fills up. That is
// stated in the UI rather than left for someone to discover and mistrust.
//
// Send message dialogue:
//   left    every staff member with a checkbox, role filter buttons (all
//           selected by default), select / deselect all, and a search box
//   right   the message being composed
//   send    a confirmation step first — how many people, broken down by branch
//           and role, how many have no email address on record, and the exact
//           message as it will arrive
//
// Push is shown as a method but is not selectable: the staff app does not
// receive push notifications yet. The server refuses it too, so this is a
// courtesy rather than the control.
// =============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";

const API = "https://api.pharmacegroup.co.uk";

function authHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

async function post(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(body || {})
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */
const fmtDateTime = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const fmtDateShort = (d) => {
  if (!d) return "";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "";
  return t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

/* -------------------------------------------------------------------------- */
/* Styles — inline, matching the rest of the head-office app                   */
/* -------------------------------------------------------------------------- */
const S = {
  wrap:       { display: "flex", gap: 14, alignItems: "stretch", minHeight: 520 },

  left:       { width: 320, flexShrink: 0, border: "1px solid #e5e7eb", borderRadius: 8,
                background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" },
  leftHead:   { padding: "10px 12px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" },
  leftList:   { overflowY: "auto", flex: 1, maxHeight: 620 },

  right:      { flex: 1, minWidth: 0, border: "1px solid #e5e7eb", borderRadius: 8,
                background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" },
  rightHead:  { padding: "12px 14px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" },
  rightBody:  { flex: 1, overflowY: "auto", maxHeight: 620, padding: "12px 14px" },

  input:      { width: "100%", boxSizing: "border-box", fontSize: 13, padding: "6px 8px",
                border: "1px solid #d1d5db", borderRadius: 5, fontFamily: "inherit" },
  select:     { fontSize: 12, padding: "4px 6px", border: "1px solid #d1d5db",
                borderRadius: 5, fontFamily: "inherit", background: "#fff", maxWidth: "100%" },

  staffRow: (on) => ({
    display: "block", width: "100%", textAlign: "left", cursor: "pointer",
    border: "none", borderBottom: "1px solid #f0f1f3", borderLeft: "3px solid " + (on ? "#0f766e" : "transparent"),
    background: on ? "#f0fdfa" : "#fff", padding: "8px 11px", fontFamily: "inherit"
  }),
  staffName:  (on) => ({ fontSize: 13.5, fontWeight: on ? 700 : 600, color: "#111827" }),
  staffMeta:  { fontSize: 11.5, color: "#6b7280", marginTop: 1 },
  staffCount: { fontSize: 10.5, color: "#6b7280", whiteSpace: "nowrap" },

  primary:    { border: "none", background: "#0f766e", color: "#fff", borderRadius: 6,
                padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                fontFamily: "inherit" },
  secondary:  { border: "1px solid #d1d5db", background: "#fff", color: "#374151", borderRadius: 6,
                padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                fontFamily: "inherit" },

  chip: (on, disabled) => ({
    border: "1px solid " + (on ? "#0f766e" : "#d1d5db"),
    background: on ? "#0f766e" : "#fff",
    color: on ? "#fff" : (disabled ? "#9ca3af" : "#374151"),
    borderRadius: 99, padding: "3px 11px", fontSize: 11.5, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", marginRight: 6, marginBottom: 6
  }),

  msgCard:    { border: "1px solid #e5e7eb", borderRadius: 7, padding: "10px 12px",
                marginBottom: 9, background: "#fff" },
  msgSubject: { fontSize: 13.5, fontWeight: 700, color: "#111827" },
  msgBody:    { fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginTop: 6,
                whiteSpace: "pre-wrap", wordBreak: "break-word" },
  msgMeta:    { fontSize: 11, color: "#9ca3af", marginTop: 6 },

  empty:      { padding: "36px 20px", textAlign: "center", color: "#9ca3af", fontSize: 13, lineHeight: 1.6 },
  note:       { fontSize: 11.5, color: "#6b7280", lineHeight: 1.45 },

  backdrop:   { position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)", zIndex: 9000,
                display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  modal:      { background: "#fff", borderRadius: 10, width: "min(1040px, 100%)",
                maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)" },
  modalHead:  { padding: "14px 18px", borderBottom: "1px solid #e5e7eb",
                display: "flex", alignItems: "center", justifyContent: "space-between" },
  modalBody:  { flex: 1, overflow: "hidden", display: "flex" },
  modalFoot:  { padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#f9fafb",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }
};

/* Method and status badges — the two things a manager scans for. */
const METHOD_BADGE = {
  email: { label: "Email", bg: "#e0f2fe", fg: "#075985" },
  push:  { label: "Push notification", bg: "#ede9fe", fg: "#5b21b6" }
};
const STATUS_BADGE = {
  sent:       { label: "Delivered", bg: "#dcfce7", fg: "#166534" },
  failed:     { label: "Failed",    bg: "#fee2e2", fg: "#991b1b" },
  queued:     { label: "Queued",    bg: "#fef3c7", fg: "#92400e" },
  no_address: { label: "No address", bg: "#fef3c7", fg: "#92400e" },
  skipped:    { label: "Skipped",   bg: "#f1f5f9", fg: "#475569" }
};

function Badge({ spec, title }) {
  if (!spec) return null;
  return (
    <span title={title} style={{
      fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 99,
      background: spec.bg, color: spec.fg, whiteSpace: "nowrap"
    }}>{spec.label}</span>
  );
}

/* ========================================================================== */
/* MAIN PANE                                                                  */
/* ========================================================================== */
export default function StaffMessages() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [pharmFilter, setPharmFilter] = useState("");

  const [selected, setSelected] = useState(null);          // the staff object
  const [messages, setMessages] = useState([]);
  const [msgState, setMsgState] = useState("idle");        // idle | loading | ready | error
  const [msgError, setMsgError] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState({});            // messageId -> bool

  const [dialogOpen, setDialogOpen] = useState(false);

  /* ---- load the staff list ------------------------------------------- */
  const loadStaff = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await post("/api/headoffice/staff-messages/staff", {});
      setStaff(d.staff || []);
      setRoles(d.roles || []);
      setPharmacies(d.pharmacies || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  /* ---- load one person's history -------------------------------------- */
  const loadMessages = useCallback(async (person, before = null) => {
    if (!person) return;
    setMsgState("loading"); setMsgError(null);
    try {
      const d = await post("/api/headoffice/staff-messages/list", {
        dbName: person.dbName, staffId: person.staffId, limit: 50, before
      });
      setMessages(prev => before ? [...prev, ...(d.messages || [])] : (d.messages || []));
      setHasMore(!!d.hasMore);
      setNextBefore(d.nextBefore || null);
      setMsgState("ready");
    } catch (e) { setMsgError(e.message); setMsgState("error"); }
  }, []);

  const selectPerson = (person) => {
    setSelected(person);
    setMessages([]); setExpanded({}); setNextBefore(null); setHasMore(false);
    loadMessages(person, null);
  };

  /* ---- left panel filtering ------------------------------------------- */
  const visibleStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter(s => {
      if (roleFilter && s.roleName !== roleFilter) return false;
      if (pharmFilter && s.dbName !== pharmFilter) return false;
      if (!q) return true;
      return (s.name || "").toLowerCase().includes(q)
        || (s.roleName || "").toLowerCase().includes(q)
        || (s.pharmacyName || "").toLowerCase().includes(q)
        || (s.email || "").toLowerCase().includes(q);
    });
  }, [staff, search, roleFilter, pharmFilter]);

  // Branch headings only earn their place when more than one branch is in view.
  const showPharmacyHeadings = useMemo(
    () => new Set(visibleStaff.map(s => s.dbName)).size > 1, [visibleStaff]);

  /* ---- after a send, refresh whatever is on screen -------------------- */
  const afterSend = useCallback(() => {
    loadStaff();
    if (selected) loadMessages(selected, null);
  }, [loadStaff, loadMessages, selected]);

  /* --------------------------------------------------------------------- */
  return (
    <div style={{ padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ maxWidth: 720 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>Messages to staff</div>
          <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.5, marginTop: 3 }}>
            Everything the platform has sent to an individual member of staff, wherever in the app
            it was sent from — schedule invites, appraisal emails, notifications and messages sent
            from here. Pick someone on the left to see their history.
          </div>
        </div>
        <button style={S.primary} onClick={() => setDialogOpen(true)}>
          ✉ Send message
        </button>
      </div>

      {/* The pane starts empty by design — say so once, plainly, rather than
          let it look broken. */}
      <div style={{ border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 7,
                    padding: "9px 12px", marginBottom: 12, fontSize: 12, color: "#1e40af",
                    lineHeight: 1.5 }}>
        <b>History starts from the day this was switched on.</b> Messages sent before then were
        recorded in several separate places with no common format, so they are not shown here.
        Everything sent from now on is.
      </div>

      {error && (
        <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>
          Could not load staff: {error}{" "}
          <button style={{ ...S.secondary, padding: "3px 10px", fontSize: 12 }} onClick={loadStaff}>
            Retry
          </button>
        </div>
      )}

      <div style={S.wrap}>
        {/* ---------------- LEFT: staff ---------------- */}
        <div style={S.left}>
          <div style={S.leftHead}>
            <input
              style={S.input}
              placeholder="Search name, role or branch…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 7 }}>
              <select style={{ ...S.select, flex: 1 }} value={roleFilter}
                      onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All roles</option>
                {roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              {pharmacies.length > 1 && (
                <select style={{ ...S.select, flex: 1 }} value={pharmFilter}
                        onChange={e => setPharmFilter(e.target.value)}>
                  <option value="">All branches</option>
                  {pharmacies.map(p => (
                    <option key={p.dbName} value={p.dbName}>{p.pharmacyName}</option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ ...S.note, marginTop: 6 }}>
              {loading ? "Loading…" : `${visibleStaff.length} of ${staff.length} staff`}
            </div>
          </div>

          <div style={S.leftList}>
            {!loading && !visibleStaff.length && (
              <div style={S.empty}>No staff match that search.</div>
            )}
            {visibleStaff.map((s, idx) => {
              const prev = visibleStaff[idx - 1];
              const heading = showPharmacyHeadings && (!prev || prev.dbName !== s.dbName);
              const on = selected && selected.staffId === s.staffId && selected.dbName === s.dbName;
              return (
                <React.Fragment key={`${s.dbName}:${s.staffId}`}>
                  {heading && (
                    <div style={{ padding: "6px 11px", background: "#f3f4f6", fontSize: 11,
                                  fontWeight: 700, color: "#4b5563", textTransform: "uppercase",
                                  letterSpacing: 0.4, position: "sticky", top: 0, zIndex: 1 }}>
                      {s.pharmacyName}
                    </div>
                  )}
                  <button style={S.staffRow(on)} onClick={() => selectPerson(s)}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.staffName(on)}>{s.name}</div>
                        <div style={S.staffMeta}>
                          {s.roleName}
                          {!showPharmacyHeadings ? "" : ""}
                          {!s.hasEmail && (
                            <span title="No email address on record — this person cannot be emailed"
                                  style={{ color: "#b45309", fontWeight: 700 }}> · no email</span>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={S.staffCount}>
                          {s.messageCount ? `${s.messageCount} sent` : "—"}
                        </div>
                        {s.lastMessageAt && (
                          <div style={{ ...S.staffCount, color: "#9ca3af" }}>
                            {fmtDateShort(s.lastMessageAt)}
                          </div>
                        )}
                        {s.failedCount > 0 && (
                          <div style={{ ...S.staffCount, color: "#dc2626", fontWeight: 700 }}>
                            {s.failedCount} failed
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ---------------- RIGHT: history ---------------- */}
        <div style={S.right}>
          {!selected ? (
            <div style={S.empty}>
              Select a member of staff to see everything they have been sent.
            </div>
          ) : (
            <>
              <div style={S.rightHead}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{selected.name}</div>
                <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 2 }}>
                  {selected.roleName} · {selected.pharmacyName}
                  {selected.email
                    ? <> · <span style={{ color: "#374151" }}>{selected.email}</span></>
                    : <span style={{ color: "#b45309", fontWeight: 600 }}> · no email address on record</span>}
                </div>
              </div>

              <div style={S.rightBody}>
                {msgState === "loading" && !messages.length && (
                  <div style={S.empty}>Loading history…</div>
                )}
                {msgState === "error" && (
                  <div style={{ color: "#dc2626", fontSize: 13 }}>
                    Could not load messages: {msgError}
                  </div>
                )}
                {msgState === "ready" && !messages.length && (
                  <div style={S.empty}>
                    Nothing has been sent to {selected.name} yet.<br />
                    <span style={{ fontSize: 12 }}>
                      Messages appear here as soon as anything is sent, from this screen or
                      anywhere else in the app.
                    </span>
                  </div>
                )}

                {messages.map(m => {
                  const long = (m.body || "").length > 320;
                  const open = !!expanded[m.id];
                  const shown = long && !open ? m.body.slice(0, 320) + "…" : m.body;
                  return (
                    <div key={m.id} style={S.msgCard}>
                      <div style={{ display: "flex", alignItems: "flex-start",
                                    justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={S.msgSubject}>{m.subject || "(no subject)"}</div>
                          <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>
                            {fmtDateTime(m.sentAt)} · {m.sourceLabel}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <Badge spec={METHOD_BADGE[m.method]}
                                 title={m.address ? `Sent to ${m.address}` : undefined} />
                          <Badge spec={STATUS_BADGE[m.status]} title={m.error || undefined} />
                        </div>
                      </div>

                      {m.body && <div style={S.msgBody}>{shown}</div>}
                      {long && (
                        <button
                          onClick={() => setExpanded(x => ({ ...x, [m.id]: !open }))}
                          style={{ border: "none", background: "none", color: "#0f766e",
                                   fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                                   padding: "4px 0 0", fontFamily: "inherit" }}>
                          {open ? "Show less" : "Show full message"}
                        </button>
                      )}

                      <div style={S.msgMeta}>
                        {m.address ? `To ${m.address}` : "No address recorded"}
                        {m.sentBy && m.sentBy !== "system" ? ` · sent by ${m.sentBy}` : " · automatic"}
                      </div>
                      {m.status === "failed" && m.error && (
                        <div style={{ fontSize: 11.5, color: "#991b1b", background: "#fef2f2",
                                      border: "1px solid #fecaca", borderRadius: 5,
                                      padding: "5px 8px", marginTop: 6 }}>
                          Delivery failed: {m.error}
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && (
                  <button
                    style={{ ...S.secondary, width: "100%", marginTop: 4 }}
                    disabled={msgState === "loading"}
                    onClick={() => loadMessages(selected, nextBefore)}>
                    {msgState === "loading" ? "Loading…" : "Load older messages"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {dialogOpen && (
        <SendMessageDialog
          staff={staff}
          roles={roles}
          pharmacies={pharmacies}
          preselect={selected}
          onClose={() => setDialogOpen(false)}
          onSent={afterSend}
        />
      )}
    </div>
  );
}

/* ========================================================================== */
/* SEND MESSAGE DIALOGUE                                                      */
/* ========================================================================== */
// Three steps, deliberately: compose → confirm → result.
//
// The confirm step is not a formality. A message from head office to a
// selected list of staff is easy to get wrong in ways that are invisible on
// the compose screen: a role filter left on from earlier, a select-all that
// caught more branches than intended, half the list with no email address. So
// the step spells out who it is going to, broken down, and shows the message
// exactly as it will arrive — before anything is sent.
function SendMessageDialog({ staff, roles, pharmacies, preselect, onClose, onSent }) {
  const [step, setStep] = useState("compose");           // compose | confirm | sending | done
  const [search, setSearch] = useState("");
  const [pharmFilter, setPharmFilter] = useState("");

  // Role filters. ALL roles start selected, per the brief — the default is
  // "everybody", and filtering is something you do deliberately.
  const [activeRoles, setActiveRoles] = useState(() => new Set(roles));
  useEffect(() => { setActiveRoles(new Set(roles)); }, [roles]);

  const key = (s) => `${s.dbName}:${s.staffId}`;

  // If someone was selected in the pane behind, start with them ticked.
  const [checked, setChecked] = useState(() => {
    const init = new Set();
    if (preselect) init.add(`${preselect.dbName}:${preselect.staffId}`);
    return init;
  });

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [method] = useState("email");                    // push is not selectable yet
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  /* ---- the list the dialogue shows ------------------------------------ */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter(s => {
      if (!activeRoles.has(s.roleName)) return false;
      if (pharmFilter && s.dbName !== pharmFilter) return false;
      if (!q) return true;
      return (s.name || "").toLowerCase().includes(q)
        || (s.roleName || "").toLowerCase().includes(q)
        || (s.pharmacyName || "").toLowerCase().includes(q);
    });
  }, [staff, activeRoles, pharmFilter, search]);

  // Ticking is by identity, not by position, so a filter change never silently
  // alters who is selected — a tick survives being filtered out of view. The
  // confirm step is where that becomes visible, which is exactly why it counts
  // the FULL selection rather than what happens to be on screen.
  const selectedStaff = useMemo(
    () => staff.filter(s => checked.has(key(s))), [staff, checked]);

  const hiddenSelectedCount = useMemo(() => {
    const vis = new Set(visible.map(key));
    return selectedStaff.filter(s => !vis.has(key(s))).length;
  }, [visible, selectedStaff]);

  const toggle = (s) => setChecked(prev => {
    const n = new Set(prev);
    const k = key(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const selectAllVisible = () => setChecked(prev => {
    const n = new Set(prev);
    visible.forEach(s => n.add(key(s)));
    return n;
  });

  const deselectAllVisible = () => setChecked(prev => {
    const n = new Set(prev);
    visible.forEach(s => n.delete(key(s)));
    return n;
  });

  const allVisibleChecked = visible.length > 0 && visible.every(s => checked.has(key(s)));

  const toggleRole = (r) => setActiveRoles(prev => {
    const n = new Set(prev);
    if (n.has(r)) n.delete(r); else n.add(r);
    return n;
  });

  /* ---- confirmation breakdown ----------------------------------------- */
  const breakdown = useMemo(() => {
    const byRole = new Map();
    const byPharmacy = new Map();
    let noEmail = 0;
    for (const s of selectedStaff) {
      byRole.set(s.roleName, (byRole.get(s.roleName) || 0) + 1);
      byPharmacy.set(s.pharmacyName, (byPharmacy.get(s.pharmacyName) || 0) + 1);
      if (!s.hasEmail) noEmail++;
    }
    return {
      total: selectedStaff.length,
      deliverable: selectedStaff.length - noEmail,
      noEmail,
      byRole: [...byRole.entries()].sort((a, b) => b[1] - a[1]),
      byPharmacy: [...byPharmacy.entries()].sort((a, b) => b[1] - a[1]),
      noEmailNames: selectedStaff.filter(s => !s.hasEmail).map(s => `${s.name} (${s.pharmacyName})`)
    };
  }, [selectedStaff]);

  const canProceed = selectedStaff.length > 0 && subject.trim() && body.trim();

  /* ---- send ------------------------------------------------------------ */
  const doSend = async () => {
    setStep("sending"); setError(null);
    try {
      const d = await post("/api/headoffice/staff-messages/send", {
        recipients: selectedStaff.map(s => ({ dbName: s.dbName, staffId: s.staffId })),
        subject: subject.trim(),
        body: body.trim(),
        method
      });
      setResult(d);
      setStep("done");
      if (onSent) onSent();
    } catch (e) {
      setError(e.message);
      setStep("confirm");
    }
  };

  /* --------------------------------------------------------------------- */
  const th = { textAlign: "left", padding: "5px 8px", fontSize: 10.5, fontWeight: 700,
               textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" };

  return (
    <div style={S.backdrop} onClick={(e) => { if (e.target === e.currentTarget && step !== "sending") onClose(); }}>
      <div style={S.modal}>
        <div style={S.modalHead}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {step === "confirm" ? "Check before sending"
                : step === "done" ? "Message sent"
                : "Send a message to staff"}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {step === "compose" && "Choose who it goes to on the left, write it on the right."}
              {step === "confirm" && "Read this back. Nothing has been sent yet."}
              {step === "sending" && "Sending — please wait."}
              {step === "done" && "A record has been added to each person's history."}
            </div>
          </div>
          <button onClick={onClose} disabled={step === "sending"}
                  style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer",
                           color: "#6b7280", lineHeight: 1, padding: 4 }}>×</button>
        </div>

        {/* ---------------- COMPOSE ---------------- */}
        {step === "compose" && (
          <div style={S.modalBody}>
            {/* left — recipients */}
            <div style={{ width: 380, flexShrink: 0, borderRight: "1px solid #e5e7eb",
                          display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                              textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 7 }}>
                  Filter by role
                </div>
                <div style={{ marginBottom: 4 }}>
                  {roles.map(r => (
                    <button key={r} style={S.chip(activeRoles.has(r))} onClick={() => toggleRole(r)}>
                      {r}
                    </button>
                  ))}
                  {roles.length > 1 && (
                    <button
                      style={{ ...S.chip(false), borderStyle: "dashed" }}
                      onClick={() => setActiveRoles(activeRoles.size === roles.length ? new Set() : new Set(roles))}>
                      {activeRoles.size === roles.length ? "Clear roles" : "All roles"}
                    </button>
                  )}
                </div>

                <input style={{ ...S.input, marginTop: 4 }} placeholder="Search staff…"
                       value={search} onChange={e => setSearch(e.target.value)} />

                {pharmacies.length > 1 && (
                  <select style={{ ...S.select, width: "100%", marginTop: 7 }}
                          value={pharmFilter} onChange={e => setPharmFilter(e.target.value)}>
                    <option value="">All branches</option>
                    {pharmacies.map(p => (
                      <option key={p.dbName} value={p.dbName}>{p.pharmacyName}</option>
                    ))}
                  </select>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                              marginTop: 9, gap: 8 }}>
                  <button
                    style={{ ...S.secondary, padding: "4px 10px", fontSize: 11.5 }}
                    onClick={allVisibleChecked ? deselectAllVisible : selectAllVisible}>
                    {allVisibleChecked ? "Deselect all shown" : "Select all shown"}
                  </button>
                  <span style={{ fontSize: 11.5, color: "#6b7280" }}>
                    {visible.length} shown
                  </span>
                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1 }}>
                {!visible.length && (
                  <div style={S.empty}>
                    No staff match those filters.<br />
                    <span style={{ fontSize: 12 }}>Try switching a role back on.</span>
                  </div>
                )}
                {visible.map(s => {
                  const on = checked.has(key(s));
                  return (
                    <label key={key(s)} style={{
                      display: "flex", alignItems: "flex-start", gap: 9, padding: "7px 12px",
                      borderBottom: "1px solid #f0f1f3", cursor: "pointer",
                      background: on ? "#f0fdfa" : "#fff"
                    }}>
                      <input type="checkbox" checked={on} onChange={() => toggle(s)}
                             style={{ marginTop: 3 }} />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#111827" }}>
                          {s.name}
                        </span>
                        <span style={{ display: "block", fontSize: 11.5, color: "#6b7280" }}>
                          {s.roleName} · {s.pharmacyName}
                          {!s.hasEmail && (
                            <span style={{ color: "#b45309", fontWeight: 700 }}> · no email</span>
                          )}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>

              <div style={{ padding: "8px 12px", borderTop: "1px solid #e5e7eb",
                            background: "#f9fafb", fontSize: 12, color: "#374151" }}>
                <b>{selectedStaff.length}</b> selected
                {hiddenSelectedCount > 0 && (
                  <span style={{ color: "#b45309" }}>
                    {" "}({hiddenSelectedCount} not currently shown)
                  </span>
                )}
              </div>
            </div>

            {/* right — the message */}
            <div style={{ flex: 1, minWidth: 0, padding: "14px 16px", overflowY: "auto" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                            textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                Send by
              </div>
              <div style={{ marginBottom: 14 }}>
                <button style={S.chip(true)} disabled>Email</button>
                <button style={S.chip(false, true)} disabled
                        title="The staff app does not receive push notifications yet">
                  Push notification — not yet available
                </button>
                <div style={{ ...S.note, marginTop: 2 }}>
                  Push notifications to the Pharmace Staff app are not live yet, so messages sent
                  from here go by email. Push messages will appear in the history alongside emails
                  once delivery is switched on.
                </div>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                            textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                Subject
              </div>
              <input style={S.input} value={subject} maxLength={200}
                     placeholder="e.g. Bank holiday opening hours"
                     onChange={e => setSubject(e.target.value)} />

              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                            textTransform: "uppercase", letterSpacing: 0.4,
                            margin: "14px 0 6px" }}>
                Message
              </div>
              <textarea
                value={body} onChange={e => setBody(e.target.value)} rows={14} maxLength={20000}
                placeholder="Write the message exactly as you want it to arrive. It is sent as plain text — no placeholders are substituted."
                style={{ ...S.input, resize: "vertical", lineHeight: 1.5, minHeight: 240 }} />
              <div style={{ ...S.note, marginTop: 4 }}>
                {body.length.toLocaleString()} characters. Sent as plain text to each person
                individually — recipients cannot see one another.
              </div>
            </div>
          </div>
        )}

        {/* ---------------- CONFIRM ---------------- */}
        {(step === "confirm" || step === "sending") && (
          <div style={{ ...S.modalBody, display: "block", overflowY: "auto", padding: "16px 18px" }}>
            {error && (
              <div style={{ color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca",
                            borderRadius: 6, padding: "9px 12px", marginBottom: 14, fontSize: 13 }}>
                Send failed: {error}
              </div>
            )}

            <div style={{ border: "1px solid #0f766e", background: "#f0fdfa", borderRadius: 8,
                          padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f766e" }}>
                {breakdown.deliverable} {breakdown.deliverable === 1 ? "person" : "people"} will
                receive this email
              </div>
              <div style={{ fontSize: 13, color: "#0f766e", marginTop: 4, lineHeight: 1.5 }}>
                {breakdown.total} selected
                {breakdown.noEmail > 0 && (
                  <> · <b>{breakdown.noEmail} will not be sent</b> — no email address on record</>
                )}
              </div>
            </div>

            {breakdown.noEmail > 0 && (
              <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 7,
                            padding: "10px 12px", marginBottom: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
                  No email address on record ({breakdown.noEmail})
                </div>
                <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.5 }}>
                  {breakdown.noEmailNames.slice(0, 12).join(", ")}
                  {breakdown.noEmailNames.length > 12 && ` and ${breakdown.noEmailNames.length - 12} more`}.
                  {" "}They are recorded in the history as not sent, so it is clear later that they
                  did not receive it.
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ flex: 1, minWidth: 240, border: "1px solid #e5e7eb", borderRadius: 7 }}>
                <div style={{ ...th, borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
                  By role
                </div>
                {breakdown.byRole.map(([r, n]) => (
                  <div key={r} style={{ display: "flex", justifyContent: "space-between",
                                        padding: "6px 10px", fontSize: 12.5,
                                        borderBottom: "1px solid #f3f4f6" }}>
                    <span>{r}</span><b>{n}</b>
                  </div>
                ))}
              </div>
              <div style={{ flex: 1, minWidth: 240, border: "1px solid #e5e7eb", borderRadius: 7 }}>
                <div style={{ ...th, borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
                  By branch
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {breakdown.byPharmacy.map(([p, n]) => (
                    <div key={p} style={{ display: "flex", justifyContent: "space-between",
                                          padding: "6px 10px", fontSize: 12.5,
                                          borderBottom: "1px solid #f3f4f6" }}>
                      <span>{p}</span><b>{n}</b>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                          textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
              Preview — exactly as it will arrive
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 7, overflow: "hidden" }}>
              <div style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb",
                            padding: "9px 12px" }}>
                <div style={{ fontSize: 11.5, color: "#6b7280" }}>
                  From: Pharmace Digital
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginTop: 3 }}>
                  {subject.trim()}
                </div>
              </div>
              <div style={{ padding: "12px 14px", fontSize: 13, lineHeight: 1.6,
                            whiteSpace: "pre-wrap", color: "#111827", maxHeight: 260,
                            overflowY: "auto" }}>
                {body.trim()}
              </div>
            </div>

            <div style={{ ...S.note, marginTop: 10 }}>
              Each person receives this individually. Once sent it cannot be recalled.
            </div>
          </div>
        )}

        {/* ---------------- DONE ---------------- */}
        {step === "done" && result && (
          <div style={{ ...S.modalBody, display: "block", overflowY: "auto", padding: "16px 18px" }}>
            <div style={{ border: "1px solid " + (result.failed ? "#fde68a" : "#86efac"),
                          background: result.failed ? "#fffbeb" : "#f0fdf4",
                          borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800,
                            color: result.failed ? "#92400e" : "#166534" }}>
                {result.sent} sent
                {result.failed ? ` · ${result.failed} failed` : ""}
                {result.noAddress ? ` · ${result.noAddress} had no address` : ""}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4,
                            color: result.failed ? "#92400e" : "#166534", lineHeight: 1.5 }}>
                Every attempt — successful or not — is recorded against that person and is visible
                in their history.
              </div>
            </div>

            {(result.results || []).some(r => r.status !== "sent") && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#374151",
                              textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
                  Not delivered
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 7, overflow: "hidden",
                              marginBottom: 14 }}>
                  {(result.results || []).filter(r => r.status !== "sent").map((r, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between",
                                          gap: 10, padding: "7px 11px", fontSize: 12.5,
                                          borderBottom: "1px solid #f3f4f6" }}>
                      <span>{r.name} <span style={{ color: "#9ca3af" }}>· {r.pharmacyName}</span></span>
                      <span style={{ color: "#991b1b", textAlign: "right" }}>
                        {r.status === "no_address" ? "No email address" : (r.error || "Failed")}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ---------------- FOOTER ---------------- */}
        <div style={S.modalFoot}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            {step === "compose" && (
              canProceed
                ? `${selectedStaff.length} recipient${selectedStaff.length === 1 ? "" : "s"} selected`
                : "Select at least one person, and fill in a subject and message."
            )}
            {step === "confirm" && "Nothing has been sent yet."}
            {step === "sending" && "Sending…"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {step === "compose" && (
              <>
                <button style={S.secondary} onClick={onClose}>Cancel</button>
                <button
                  style={{ ...S.primary, opacity: canProceed ? 1 : 0.5,
                           cursor: canProceed ? "pointer" : "not-allowed" }}
                  disabled={!canProceed}
                  onClick={() => setStep("confirm")}>
                  Continue →
                </button>
              </>
            )}
            {step === "confirm" && (
              <>
                <button style={S.secondary} onClick={() => setStep("compose")}>← Back to edit</button>
                <button style={S.primary} onClick={doSend}>
                  Confirm and send to {breakdown.deliverable}{" "}
                  {breakdown.deliverable === 1 ? "person" : "people"}
                </button>
              </>
            )}
            {step === "sending" && (
              <button style={{ ...S.primary, opacity: 0.6, cursor: "wait" }} disabled>Sending…</button>
            )}
            {step === "done" && (
              <button style={S.primary} onClick={onClose}>Close</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
